<template>
  <div
    class="min-h-screen bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8"
  >
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <div
        class="bg-gray-800 py-8 px-6 shadow-md sm:rounded-lg sm:px-10 border border-gray-700"
      >
        <!-- Loading state -->
        <div v-if="isVerifying" class="text-center">
          <div
            class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"
          ></div>
          <h2 class="text-2xl font-extrabold text-white mb-2">
            Verifikacija email-a
          </h2>
          <p class="text-gray-400">
            Molimo sačekajte dok verifikujemo vaš email...
          </p>
        </div>

        <!-- Success state -->
        <div v-else-if="verificationStatus === 'success'" class="text-center">
          <div class="text-green-400 text-6xl mb-4">✓</div>
          <h2 class="text-2xl font-extrabold text-white mb-2">
            Email uspešno verifikovan!
          </h2>
          <p class="text-gray-400 mb-6">
            Vaš email je uspešno potvrđen. Sada možete da se prijavite na vaš
            nalog.
          </p>
          <FormButton @click="goToLogin" class="w-full">
            Idite na prijavu
          </FormButton>
        </div>

        <!-- Error state -->
        <div v-else-if="verificationStatus === 'error'" class="text-center">
          <div class="text-red-400 text-6xl mb-4">✗</div>
          <h2 class="text-2xl font-extrabold text-white mb-2">
            Greška pri verifikaciji
          </h2>
          <p class="text-gray-400 mb-6">{{ errorMessage }}</p>

          <!-- Server error message -->
          <div
            v-if="serverError"
            class="bg-red-900 border border-red-700 text-red-300 px-3 py-2 rounded relative text-sm mb-4"
            role="alert"
          >
            <span class="block sm:inline">{{ serverError }}</span>
          </div>

          <div class="space-y-3">
            <div v-if="showResendOption">
              <FormInput
                id="resend-email"
                name="email"
                type="email"
                label="Email adresa"
                v-model="resendEmail"
                placeholder="Unesite vašu email adresu"
                :error="emailError"
              />
              <FormButton
                @click="handleResendVerification"
                :loading="isResending"
                class="w-full mt-3"
              >
                Pošaljite ponovo
              </FormButton>
            </div>
            <button
              v-else
              @click="showResendOption = true"
              class="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
            >
              Pošaljite verifikaciju ponovo
            </button>
            <router-link
              to="/login"
              class="block text-center text-indigo-400 hover:text-indigo-300"
            >
              Nazad na prijavu
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import FormButton from '@/components/auth/FormButton.vue';
import FormInput from '@/components/auth/FormInput.vue';
import api from '@/services/api';
import formValidator from '@/services/formValidator';

export default defineComponent({
  name: 'EmailVerificationView',
  components: {
    FormButton,
    FormInput,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const isVerifying = ref(true);
    const verificationStatus = ref<'success' | 'error' | null>(null);
    const errorMessage = ref('');
    const serverError = ref('');
    const isResending = ref(false);
    const showResendOption = ref(false);
    const resendEmail = ref('');
    const emailError = ref('');

    const verifyEmail = async (token: string) => {
      try {
        const response = await api.post('/api/auth/verify-email', { token });

        if (response.data.status === 'success') {
          verificationStatus.value = 'success';
        } else {
          verificationStatus.value = 'error';
          errorMessage.value =
            response.data.message || 'Došlo je do greške pri verifikaciji';
        }
      } catch (error: any) {
        verificationStatus.value = 'error';
        if (error.response?.data?.message) {
          errorMessage.value = error.response.data.message;
        } else {
          errorMessage.value =
            'Token za verifikaciju je neispravan ili je istekao';
        }
      } finally {
        isVerifying.value = false;
      }
    };

    const handleResendVerification = async () => {
      // Validate email
      const emailValidation = formValidator.validateEmail(
        resendEmail.value,
        false
      );
      if (!emailValidation.isValid) {
        emailError.value = emailValidation.error;
        return;
      }
      emailError.value = '';

      isResending.value = true;
      serverError.value = '';

      try {
        const response = await api.post('/api/auth/resend-verification', {
          email: resendEmail.value,
        });

        if (response.data.status === 'success') {
          // Show success message
          errorMessage.value =
            'Verifikacioni email je uspešno poslat. Proverite vašu email adresu.';
          showResendOption.value = false;
        }
      } catch (error: any) {
        if (error.response?.data?.message) {
          serverError.value = error.response.data.message;
        } else {
          serverError.value =
            'Došlo je do greške pri slanju verifikacionog email-a';
        }
      } finally {
        isResending.value = false;
      }
    };

    const goToLogin = () => {
      router.push('/login');
    };

    onMounted(() => {
      const token = route.query.token as string;
      if (token) {
        verifyEmail(token);
      } else {
        verificationStatus.value = 'error';
        errorMessage.value = 'Nema token-a za verifikaciju u URL-u';
        isVerifying.value = false;
      }
    });

    return {
      isVerifying,
      verificationStatus,
      errorMessage,
      serverError,
      isResending,
      showResendOption,
      resendEmail,
      emailError,
      handleResendVerification,
      goToLogin,
    };
  },
});
</script>
